import {
  Badge,
  Box,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  type PopoverOrigin,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { Close, NotificationsNoneRounded } from '@mui/icons-material';
import moment from 'moment';
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
  useInAppNotificationsMarkSeenMutation,
  useMyInAppNotificationsQuery,
} from '../../../lib/graphql';
import { userState } from '../../../state';
import { NotificationPromptActions } from '../../notifications/notification-prompt-actions';

interface NotificationBellProps {
  /** Styling for the bell IconButton (e.g. icon color on a colored bar). */
  sx?: SxProps<Theme>;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

/**
 * Relative timestamp shown on each notification, matching the chat list style.
 * @param date
 */
function renderNotificationDate(date?: string | null): string {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  if (m.isSame(moment(), 'day')) return m.format('LT');
  if (m.isSame(moment(), 'week')) return m.format('ddd, hA');
  if (m.isSame(moment(), 'year')) return m.format('MMM D, hA');
  return m.format('MMM D, YYYY');
}

/** Tracks document visibility so polling pauses when the tab is backgrounded. */
function useTabVisible() {
  const [visible, setVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

export function NotificationBell({
  sx = undefined,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}: NotificationBellProps) {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();
  const isTabVisible = useTabVisible();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { data, refetch } = useMyInAppNotificationsQuery({
    skip: !user,
    fetchPolicy: 'cache-and-network',
    pollInterval: isTabVisible ? 30_000 : 0,
    variables: { limit: 20 },
  });
  const [markSeen] = useInAppNotificationsMarkSeenMutation();

  const feed = data?.myInAppNotifications;
  const unseenCount = feed?.unseenCount ?? 0;
  const items = (feed?.items ?? []).filter(
    (item): item is NonNullable<typeof item> => Boolean(item),
  );

  const handleOpen = async (e: MouseEvent<HTMLElement>) => {
    // A TOGGLE, not an open. On a phone the bell is the obvious place to tap
    // to put the panel away again, and re-running the open path there just
    // re-anchored it — the panel stayed put and the tap looked dead. The
    // backdrop usually swallows that tap first, but it does not have to for
    // this to behave.
    if (anchorEl) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(e.currentTarget);
    // Opening the bell clears the badge: mark everything seen, then refresh.
    if (unseenCount > 0) {
      try {
        await markSeen({ variables: { ids: null } });
        await refetch();
      } catch {
        // non-fatal — the badge will simply reappear on the next poll
      }
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (ctaUrl?: string | null) => {
    handleClose();
    if (!ctaUrl) return;
    try {
      const url = new URL(ctaUrl);
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search);
      } else {
        window.open(ctaUrl, '_blank', 'noopener');
      }
    } catch {
      // Treat a non-absolute value as an in-app path.
      navigate(ctaUrl);
    }
  };

  if (!user) return null;

  return (
    <>
      <IconButton onClick={handleOpen} sx={sx} aria-label="Notifications">
        <Badge badgeContent={unseenCount} color="error" max={99}>
          <NotificationsNoneRounded sx={{ fontSize: 28 }} />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          paper: { sx: { borderRadius: 2, width: 360, maxWidth: '90vw' } },
        }}
      >
        {/* An explicit close, because the only other way out is the
            invisible backdrop — and on a phone, where the panel fills most
            of the screen, "tap the sliver of page behind it" is not a
            discoverable way to dismiss anything. */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close notifications"
            sx={{ mr: -0.5 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              You&apos;re all caught up.
            </Typography>
          </Box>
        ) : (
          <MenuList dense sx={{ maxHeight: 420, overflowY: 'auto', py: 0 }}>
            {items.map((item) => (
              <MenuItem
                key={item._id}
                onClick={() => handleItemClick(item.ctaUrl)}
                sx={{
                  alignItems: 'flex-start',
                  whiteSpace: 'normal',
                  px: 2,
                  py: 1.25,
                  ...(item.seenAt ? {} : { bgcolor: 'action.hover' }),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: item.seenAt ? 'transparent' : 'error.main',
                    mt: 0.9,
                    mr: 1.25,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: item.seenAt ? 500 : 600,
                        // A notification asking for an answer is shown in
                        // full: the customer has to be able to read what
                        // they're confirming. The rest stay one line.
                        ...(item.prompt
                          ? { overflowWrap: 'anywhere' }
                          : {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }),
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      {renderNotificationDate(item.createdAt)}
                    </Typography>
                  </Box>
                  {item.body && item.body !== item.title && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.25,
                        ...(item.prompt
                          ? {
                              whiteSpace: 'pre-line',
                              overflowWrap: 'anywhere',
                            }
                          : {
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }),
                      }}
                    >
                      {item.body}
                    </Typography>
                  )}
                  {/* The ask itself only while it can still be answered here:
                      once it's answered or has lapsed, "please confirm" is
                      no longer something the customer can act on. */}
                  {item.promptText &&
                    item.prompt?.status === 'pending' &&
                    item.prompt.canAnswer && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.75, overflowWrap: 'anywhere' }}
                      >
                        {item.promptText}
                      </Typography>
                    )}
                  {item.prompt && (
                    <Box sx={{ mt: 1 }}>
                      <NotificationPromptActions
                        prompt={item.prompt}
                        via="in_app"
                      />
                    </Box>
                  )}
                </Box>
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Popover>
    </>
  );
}
